<script setup lang="ts">
import { defineOptions } from 'vue'
defineOptions({ name: 'DashboardComponent' })
import { computed, ref } from 'vue'
import { useDashboardData, type Entity } from '@/composables/useDashboardData'
import MetricCard from '@/components/dashboard/MetricCard.vue'
import EntityBarChart from '@/components/dashboard/EntityBarChart.vue'
import YearProgressCard from '@/components/dashboard/YearProgressCard.vue'
import YearShareCard from '@/components/dashboard/YearShareCard.vue'
import RecentOrdersTable from '@/components/dashboard/RecentOrdersTable.vue'
import OrderDataService from '@/services/OrderDataService'
import PageBreadcrumb from '@/components/common/PageBreadcrumb.vue'
import { HomeIcon, UsersIcon, ShoppingBagIcon, PackageIcon } from 'lucide-vue-next'

const ENTITIES: Entity[] = ['companies', 'users', 'orders', 'mails']

const { totals, monthly, byYear, entityYears, loading, error, retry, entityLabels } = useDashboardData()

const COLORS: Record<string, string> = {
  companies: '#465fff',
  users: '#0ba5ec',
  orders: '#12b76a',
  mails: '#fb6514',
}

const orders = ref<any[]>([])
async function loadOrders() {
  try {
    const resp: any = await OrderDataService.getAllOrders(1, 7)
    orders.value = resp?.data?.data?.data ?? []
  } catch {
    orders.value = []
  }
}
loadOrders()

const currentMonthIndex = new Date().getMonth()
const currentMonthOrders = computed(() => monthly.value.orders[currentMonthIndex] ?? 0)
const monthlyAvgOrders = computed(() => {
  const arr = monthly.value.orders
  return arr.reduce((a, b) => a + b, 0) / (arr.length || 1)
})

const yearShareRows = computed(() => {
  const currentYear = byYear.value.find((y) => y.year === new Date().getFullYear())
  return (['companies', 'users', 'orders', 'mails'] as const).map((e) => ({
    label: entityLabels[e],
    current: monthly.value[e][currentMonthIndex] ?? 0,
    total: currentYear?.series[e] ?? 0,
    color: COLORS[e],
  }))
})
</script>

<template>
  <div class="space-y-6">
    <PageBreadcrumb page-title="Prehľad" />

    <div v-if="error" class="rounded-xl border border-error-200 bg-error-50 p-4 text-error-700 dark:border-error-800 dark:bg-error-950/30 dark:text-error-400">
      <p>Chyba pri načítaní dát.</p>
      <button class="mt-2 text-sm font-medium underline" @click="retry()">Skúsiť znova</button>
    </div>

    <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <MetricCard title="Spoločnosti" :value="totals.companies" :icon="HomeIcon" />
      <MetricCard title="Klienti" :value="totals.users" :icon="UsersIcon" />
      <MetricCard title="Objednávky" :value="totals.orders" :icon="ShoppingBagIcon" />
      <MetricCard title="Zásielky" :value="totals.mails" :icon="PackageIcon" />
    </div>

    <div class="grid grid-cols-1 gap-4 xl:grid-cols-2">
      <template v-if="!loading">
        <EntityBarChart
          v-for="entity in ENTITIES"
          :key="entity"
          :title="entityLabels[entity]"
          :rows="entityYears[entity]"
        />
      </template>
      <template v-else>
        <div
          v-for="n in 4"
          :key="n"
          class="h-80 animate-pulse rounded-xl bg-gray-100 dark:bg-gray-800"
        />
      </template>
    </div>

    <div class="grid grid-cols-1 gap-4 lg:grid-cols-3">
      <div class="lg:col-span-2">
        <RecentOrdersTable :orders="orders" />
      </div>
      <div class="space-y-4">
        <YearProgressCard :current="currentMonthOrders" :average="monthlyAvgOrders" />
        <YearShareCard :rows="yearShareRows" />
      </div>
    </div>
  </div>
</template>
